import { useCallback, useEffect, useState } from 'react';
import { Button, Card, Modal } from 'react-bootstrap';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../services/AxiosInstance';
import Api from '../../../../constants/Api';
import MenuConfig from '../../../../constants/MenuConfig';
import { errorAlert, successAlert, warnAlert } from '../../alert/defaultAlert';
import { formatDateTime } from '../../../../utils/DateUtils';
import { formatNumberWithTwoDecimals, formatPhoneNumber, buildWhatsappUrl } from '../../../../utils/FormatUtils';
import AppointmentCurrentStatus from './AppointmentCurrentStatus';
import AppointmentDepositPanel from './AppointmentDeposit';
import ModalPageEvent from '../../Common/Modal/ModalPageEvent';
import TextArea from '../../Forms/Element/TextArea';
import ClubCrownIcon from '../../Icon/ClubCrownIcon';
import ClubLateIcon from '../../Icon/ClubLateIcon';
import CustomerLatePaymentAlert from '../Main/Customer/CustomerLatePaymentAlert';
import CustomerQuickCreateModal from '../Main/Customer/CustomerQuickCreateModal';
import '../../../pages/appointment/appointment.css';

const getDisplayName = (person, fallback = '-') => {
    if (!person) return fallback;

    const fullName = person.fullName?.trim();
    if (fullName) return fullName;

    const composedName = [person.firstName, person.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();

    return composedName || person.name || fallback;
};

const getServiceLabel = (service, fallback = '-') => service?.name || service?.description || fallback;

const getDurationInMinutes = (appointment) => {
    const duration = Number(appointment?.duration ?? appointment?.durationMinutes);
    if (Number.isFinite(duration) && duration > 0) {
        return duration;
    }

    // Registros antigos podem não ter duration preenchido, mas ainda possuem o fim
    // calculado. Usar essa diferença evita mostrar o mesmo horário inicial e final.
    if (appointment?.scheduledDate && appointment?.endTime) {
        const start = moment(appointment.scheduledDate);
        const end = moment(appointment.endTime);
        const derivedDuration = end.diff(start, 'minutes');
        if (derivedDuration > 0) {
            return derivedDuration;
        }
    }

    const serviceItemsDuration = Array.isArray(appointment?.serviceItems)
        ? appointment.serviceItems.reduce((total, item) => {
            const serviceDuration = Number(item?.service?.duration);
            return total + (Number.isFinite(serviceDuration) && serviceDuration > 0 ? serviceDuration : 0);
        }, 0)
        : 0;
    if (serviceItemsDuration > 0) {
        return serviceItemsDuration;
    }

    const primaryServiceDuration = Number(appointment?.service?.duration);
    if (Number.isFinite(primaryServiceDuration) && primaryServiceDuration > 0) {
        return primaryServiceDuration;
    }

    return 0;
};

const formatAppointmentDuration = (appointment) => {
    const duration = getDurationInMinutes(appointment);
    return duration ? `${duration} minutos` : '-';
};

const parseAppointmentPrice = (price) => {
    if (price == null || price === '') return null;
    if (typeof price === 'number') return Number.isFinite(price) ? price : null;

    const rawValue = String(price).trim().replace(/\s+/g, '').replace(/^R\$/i, '');
    if (!rawValue) return null;

    let normalizedValue = rawValue;
    const hasComma = normalizedValue.includes(',');
    const hasDot = normalizedValue.includes('.');

    if (hasComma && hasDot) {
        normalizedValue = normalizedValue.lastIndexOf(',') > normalizedValue.lastIndexOf('.')
            ? normalizedValue.replace(/\./g, '').replace(',', '.')
            : normalizedValue.replace(/,/g, '');
    } else if (hasComma) {
        normalizedValue = normalizedValue.replace(',', '.');
    }

    const parsedValue = Number(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : null;
};

const getAppointmentPriceValue = (appointment) => parseAppointmentPrice(appointment?.price ?? appointment?.service?.price);

const formatAppointmentPrice = (price) => {
    const parsedPrice = parseAppointmentPrice(price);
    return parsedPrice != null ? `R$ ${formatNumberWithTwoDecimals(parsedPrice)}` : '-';
};

const AppointmentDetailItem = ({ icon, label, value, subvalue, wide = false, code = false }) => (
    <div className={`appointment-detail-item ${wide ? 'appointment-detail-item-wide' : ''}`}>
        <span className="appointment-detail-label">
            <i className={icon}></i>
            <span>{label}</span>
        </span>
        <strong className={`appointment-detail-value ${code ? 'appointment-detail-code' : ''}`}>{value}</strong>
        {subvalue ? <span className="appointment-detail-subvalue">{subvalue}</span> : null}
    </div>
);

const AppointmentDetailsModal = ({ show, appointment, onHide, onAppointmentUpdated, onReschedule, onEdit, onAddServices, onEncaixe }) => {
    const navigate = useNavigate();
    const [currentAppointment, setCurrentAppointment] = useState(appointment || null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showStartModal, setShowStartModal] = useState(false);
    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showNoShowModal, setShowNoShowModal] = useState(false);
    const [showDurationModal, setShowDurationModal] = useState(false);
    const [showCustomerEdit, setShowCustomerEdit] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    // WHY: "venda rápida" (parâmetro do agendamento) troca os 3 passos (confirmar/iniciar/finalizar)
    // por um único botão que conclui o atendimento de uma vez e libera o "Gerar Venda".
    // null = ainda não sabemos (consulta em andamento). Ver o useEffect que carrega o parâmetro.
    const [expressSale, setExpressSale] = useState(null);
    // WHY: resumo da venda vinculada — sinaliza se já foi gerada (some o botão "Gerar Venda")
    // e descreve o atendimento realizado, que é o que a venda cobrou. Quem marcou corte + barba
    // e trocou a barba na cadeira precisa ver isso aqui, não a lista do agendamento.
    const [saleAlreadyGenerated, setSaleAlreadyGenerated] = useState(false);
    const [salePerformedItems, setSalePerformedItems] = useState([]);
    const [saleRemovedItems, setSaleRemovedItems] = useState([]);
    // Um agendamento pode ter vários serviços; carregamos a lista completa ao abrir (by-uuid).
    const [serviceItems, setServiceItems] = useState([]);
    const [sendingWhatsappConfirmation, setSendingWhatsappConfirmation] = useState(false);
    const [repurchaseOpportunities, setRepurchaseOpportunities] = useState([]);

    useEffect(() => {
        setCurrentAppointment(appointment || null);
    }, [appointment]);

    useEffect(() => {
        if (!show || !appointment?.uuid) {
            setServiceItems([]);
            return;
        }
        axiosInstance.get(`${Api.appointment.byUuid}/${appointment.uuid}`)
            .then((resp) => setServiceItems(Array.isArray(resp.data?.serviceItems) ? resp.data.serviceItems : []))
            .catch(() => setServiceItems([]));
    }, [show, appointment?.uuid]);

    useEffect(() => {
        if (!show || !currentAppointment?.id) {
            setRepurchaseOpportunities([]);
            return;
        }
        // A consulta acontece somente ao abrir o detalhe, evitando aumentar o custo
        // da carga em lote de todos os agendamentos exibidos no calendário.
        axiosInstance.get(`${Api.appointment.appointment}/${currentAppointment.id}/repurchase-opportunities`)
            .then((resp) => setRepurchaseOpportunities(Array.isArray(resp.data) ? resp.data : []))
            .catch(() => setRepurchaseOpportunities([]));
    }, [show, currentAppointment?.id]);

    // Recarrega a cada abertura, e não uma única vez na montagem da tela: se a consulta falhasse
    // no carregamento do dashboard, a venda rápida ficava desligada até dar F5, porque nada
    // tentava de novo. Enquanto a resposta não chega, expressSale fica null (indefinido) e os
    // botões de avanço do atendimento não são exibidos, para o operador não ver e clicar no
    // fluxo errado por causa de uma consulta ainda em andamento.
    useEffect(() => {
        if (!show) {
            return;
        }

        axiosInstance.get(`${Api.appointment.appointment}/express-sale-enabled`)
            .then((resp) => setExpressSale(resp.data === true))
            .catch(() => setExpressSale(false));
    }, [show]);

    // Só faz sentido buscar a venda quando o atendimento está concluído (estado que libera "Gerar Venda").
    useEffect(() => {
        if (currentAppointment?.id && currentAppointment.appointmentStatus === 'COMPLETED') {
            axiosInstance.get(`${Api.appointment.appointment}/${currentAppointment.id}/sale`)
                .then((resp) => {
                    setSaleAlreadyGenerated(Boolean(resp.data?.hasSale));
                    setSalePerformedItems(Array.isArray(resp.data?.performedItems) ? resp.data.performedItems : []);
                    setSaleRemovedItems(Array.isArray(resp.data?.removedItems) ? resp.data.removedItems : []);
                })
                .catch(() => {
                    setSaleAlreadyGenerated(false);
                    setSalePerformedItems([]);
                    setSaleRemovedItems([]);
                });
        } else {
            setSaleAlreadyGenerated(false);
            setSalePerformedItems([]);
            setSaleRemovedItems([]);
        }
    }, [currentAppointment?.id, currentAppointment?.appointmentStatus]);

    const refreshAppointment = useCallback(async () => {
        if (!appointment?.id) return null;

        const response = await axiosInstance.get(`${Api.appointment.appointment}/${appointment.id}`);
        setCurrentAppointment(response.data);
        onAppointmentUpdated?.(response.data);
        return response.data;
    }, [appointment?.id, onAppointmentUpdated]);

    const handleClose = useCallback(() => {
        setShowConfirmModal(false);
        setShowStartModal(false);
        setShowCompleteModal(false);
        setShowCancelModal(false);
        setShowNoShowModal(false);
        setShowDurationModal(false);
        setShowCustomerEdit(false);
        setCancelReason('');
        onHide?.();
    }, [onHide]);

    const handleError = (error) => {
        errorAlert(error.response?.data?.message || error.message);
    };

    const handleConfirm = () => {
        if (!currentAppointment) return;

        axiosInstance.patch(`${Api.appointment.appointment}/${currentAppointment.id}/confirm`)
            .then(async () => {
                successAlert('Operação realizada com sucesso');
                await refreshAppointment();
                setShowConfirmModal(false);
            })
            .catch(handleError);
    };

    const handleSendWhatsappConfirmation = () => {
        if (!currentAppointment?.id) return;
        // A janela é aberta durante o clique para evitar que o navegador bloqueie o popup
        // enquanto o backend prepara o token e a mensagem de confirmação.
        const whatsappWindow = window.open('about:blank', 'appointment-whatsapp-confirmation');
        setSendingWhatsappConfirmation(true);
        axiosInstance.post(`${Api.appointment.appointment}/${currentAppointment.id}/whatsapp-confirmation`)
            .then(async (response) => {
                const phone = response.data?.phone || '';
                const message = response.data?.message || '';
                const whatsappUrl = buildWhatsappUrl(phone, message);
                if (whatsappWindow) {
                    whatsappWindow.location.href = whatsappUrl;
                } else {
                    window.location.href = whatsappUrl;
                }
                await refreshAppointment();
            })
            .catch((error) => {
                whatsappWindow?.close();
                handleError(error);
            })
            .finally(() => setSendingWhatsappConfirmation(false));
    };

    const handleStart = () => {
        if (!currentAppointment) return;

        axiosInstance.patch(`${Api.appointment.appointment}/${currentAppointment.id}/start`)
            .then(async () => {
                successAlert('Operação realizada com sucesso');
                await refreshAppointment();
                setShowStartModal(false);
            })
            .catch(handleError);
    };

    const handleComplete = () => {
        if (!currentAppointment) return;

        axiosInstance.patch(`${Api.appointment.appointment}/${currentAppointment.id}/complete`)
            .then(async () => {
                successAlert('Operação realizada com sucesso');
                await refreshAppointment();
                setShowCompleteModal(false);
            })
            .catch(handleError);
    };

    const handleAdjustDuration = (duration) => {
        if (!currentAppointment) return;

        axiosInstance.patch(`${Api.appointment.appointment}/${currentAppointment.id}/duration`, null, {
            params: { duration }
        })
            .then(async () => {
                successAlert(`Duração ajustada para ${duration} minutos`);
                await refreshAppointment();
                setShowDurationModal(false);
            })
            .catch(handleError);
    };

    const handleExpressComplete = () => {
        if (!currentAppointment) return;

        // Conclui toda a cadeia de status no backend de uma vez; ao atualizar, o agendamento
        // fica COMPLETED e o botão "Gerar Venda" aparece automaticamente neste mesmo modal.
        axiosInstance.patch(`${Api.appointment.appointment}/${currentAppointment.id}/express-complete`)
            .then(async () => {
                successAlert('Atendimento concluído');
                await refreshAppointment();
            })
            .catch(handleError);
    };

    const handleCancel = () => {
        if (!currentAppointment) return;
        if (!cancelReason.trim()) {
            warnAlert('Por favor, informe o motivo do cancelamento');
            return;
        }

        axiosInstance.patch(`${Api.appointment.appointment}/${currentAppointment.id}/cancel`, {
            reason: cancelReason
        })
            .then(async () => {
                successAlert('Operação realizada com sucesso');
                await refreshAppointment();
                setShowCancelModal(false);
                setCancelReason('');
            })
            .catch(handleError);
    };

    // Editar: abre o painel de edição da própria agenda, sem sair da tela. O operador trabalha
    // na agenda, então tirá-lo dela para corrigir um serviço custa o contexto do dia inteiro.
    const handleEdit = () => {
        if (!currentAppointment) return;

        onEdit?.(currentAppointment);
    };

    // Só faz sentido oferecer para quem ainda não é do clube. Quem já assina aparece com a
    // coroa ao lado do nome, e uma segunda assinatura para o mesmo cliente geraria duas
    // cobranças recorrentes.
    //
    // O inadimplente entra em clubMember (é do clube, só está devendo), então já fica fora
    // da oferta por essa condição. O clubLate é checado junto como defesa: oferecer
    // assinatura a quem tem contrato em atraso criaria justamente a segunda recorrência
    // que este bloqueio existe para evitar. O caminho dele é regularizar, não assinar de novo.
    const customer = currentAppointment?.customer;
    const canOfferSubscription = Boolean(customer?.id) && !customer?.clubMember && !customer?.clubLate;

    /**
     * Leva para o cadastro de assinatura com o cliente já selecionado.
     *
     * A assinatura não é criada aqui de propósito: ela exige escolher o plano e tokenizar
     * o cartão no SDK do provedor (Celcoin). Reproduzir isso dentro do modal
     * duplicaria a captura de dados de cartão, que é justamente o que não se deve espalhar
     * pela aplicação.
     */
    const handleMakeSubscriber = () => {
        if (!canOfferSubscription) return;

        const customerName = getDisplayName(customer);
        const customerInfo = customer.phone || customer.document || '';

        navigate(`${MenuConfig.subscription.subscription.route}/new`, {
            state: {
                customer: {
                    value: customer.id,
                    label: customerInfo ? `${customerName} (${customerInfo})` : customerName,
                    customer
                },
                // Ao terminar (ou cancelar), o operador volta para a agenda de onde saiu.
                returnTo: MenuConfig.appointment.dashboard.route
            }
        });
    };

    /**
     * Depois de atualizar o cliente, relê o atendimento: o nome e o telefone mostrados aqui e
     * no cartão da agenda vêm do agendamento, e refreshAppointment avisa a tela de fora.
     */
    const handleCustomerUpdated = () => {
        refreshAppointment()
            .then(() => successAlert('Cliente atualizado com sucesso.'))
            .catch(handleError);
    };

    // Encaixe: abre o painel da agenda com o mesmo horário, profissional e filial deste
    // atendimento, para lançar um cliente por cima deste horário. Cliente e serviço ficam
    // vazios, pois são de outra pessoa. Antes isso levava para o formulário em outra tela.
    const handleEncaixe = () => {
        if (!currentAppointment) return;

        onEncaixe?.(currentAppointment);
    };

    const handleNoShow = () => {
        if (!currentAppointment) return;

        axiosInstance.patch(`${Api.appointment.appointment}/${currentAppointment.id}/no-show`)
            .then(async () => {
                successAlert('Operação realizada com sucesso');
                await refreshAppointment();
                setShowNoShowModal(false);
            })
            .catch(handleError);
    };

    if (!currentAppointment) return null;

    // Todos os serviços do agendamento; cai no serviço único quando não há itens (agendamentos antigos).
    const servicesLabel = serviceItems.length > 0
        ? serviceItems.map((item) => getServiceLabel(item.service)).join(' + ')
        : getServiceLabel(currentAppointment.service);
    const appointmentDuration = getDurationInMinutes({ ...currentAppointment, serviceItems });

    return (
        <>
            <Modal
                show={show}
                onHide={handleClose}
                centered
                size="lg"
                dialogClassName="appointment-details-modal"
            >
                <Card className="appointment-dashboard-details-card is-modal">
                    <Card.Header className="appointment-dashboard-details-card__header">
                        <div className="appointment-dashboard-details-card__top">
                            <div className="appointment-dashboard-details-card__title-wrap">
                                <div className="appointment-dashboard-surface__icon is-accent">
                                    <i className="fas fa-info-circle"></i>
                                </div>
                                <div>
                                    <Card.Title className="mb-0">Detalhes do agendamento</Card.Title>
                                    <small>
                                        {formatDateTime(currentAppointment.scheduledDate)} • {servicesLabel}
                                    </small>
                                </div>
                            </div>
                            <AppointmentCurrentStatus row={currentAppointment} />
                        </div>
                    </Card.Header>
                    <Card.Body className="appointment-dashboard-details-card__body">
                        {currentAppointment.encaixe && (
                            // Sinaliza que o agendamento foi criado como encaixe, por cima de outro horário.
                            <div className="mb-3">
                                <span className="badge d-inline-flex align-items-center" style={{ gap: '0.35rem', fontSize: '0.85rem', padding: '0.5rem 0.75rem', backgroundColor: '#fd7e14', color: '#fff' }}>
                                    <i className="fa-solid fa-arrows-to-dot"></i>
                                    Encaixe
                                </span>
                            </div>
                        )}
                        {/* Sem o invólucro: o próprio alerta já tem margem inferior e ocupa a linha
                            inteira, e um div fixo deixava um vão em branco quando não há aviso. */}
                        {currentAppointment.customer && (
                            <CustomerLatePaymentAlert
                                customer={currentAppointment.customer}
                                // Pagou no balcão e ficou em dia: o detalhe para de mostrar o
                                // cliente como em atraso sem precisar reabrir o agendamento.
                                onRegularized={() => setCurrentAppointment((prev) => ({
                                    ...prev,
                                    customer: { ...prev.customer, clubLate: false }
                                }))}
                            />
                        )}
                        {repurchaseOpportunities.length > 0 && (
                            <section className="appointment-repurchase">
                                <div className="appointment-repurchase__header">
                                    <div>
                                        <span className="appointment-repurchase__eyebrow">Oportunidade comercial</span>
                                        <h6>Itens no período de recompra</h6>
                                        <p>O prazo recomendado já foi atingido. Aproveite o atendimento para oferecer estes itens.</p>
                                    </div>
                                    <span className="appointment-repurchase__count">{repurchaseOpportunities.length}</span>
                                </div>
                                <div className="appointment-repurchase__list">
                                    {repurchaseOpportunities.map((opportunity) => (
                                        <div className="appointment-repurchase__item" key={opportunity.productId}>
                                            <span className="appointment-repurchase__icon">
                                                <i className={opportunity.productType === 'SERVICE'
                                                    ? 'fa-solid fa-scissors'
                                                    : 'fa-solid fa-box-open'}></i>
                                            </span>
                                            <span className="appointment-repurchase__content">
                                                <small>{opportunity.productType === 'SERVICE' ? 'Serviço' : 'Produto'}</small>
                                                <strong>{opportunity.productName}</strong>
                                                <span>
                                                    Última compra em {moment(opportunity.lastPurchaseDate).format('DD/MM/YYYY')}
                                                    {' • '}ciclo de {opportunity.intervalDays} dias
                                                </span>
                                            </span>
                                            <span className="appointment-repurchase__overdue">
                                                {opportunity.overdueDays > 0
                                                    ? `${opportunity.overdueDays} ${opportunity.overdueDays === 1 ? 'dia' : 'dias'} em atraso`
                                                    : 'Vence hoje'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}
                        <div className="appointment-detail-grid">
                            <AppointmentDetailItem
                                icon="fa-regular fa-user"
                                label="Cliente"
                                value={
                                    <>
                                        {getDisplayName(currentAppointment.customer)}
                                        {currentAppointment.customer?.clubMember && (
                                            <ClubCrownIcon />
                                        )}
                                        {currentAppointment.customer?.clubLate && (
                                            <ClubLateIcon />
                                        )}
                                        {/* Corrigir nome, telefone ou CPF na hora do atendimento,
                                            no mesmo formulário do Novo cliente, sem ir até
                                            Cadastros > Clientes e perder o atendimento de vista. */}
                                        {customer?.id && (
                                            <button
                                                type="button"
                                                className="appointment-detail-inline-action"
                                                onClick={() => setShowCustomerEdit(true)}
                                                title="Atualizar os dados do cliente"
                                            >
                                                <i className="fas fa-pen"></i>
                                                Atualizar
                                            </button>
                                        )}
                                        {/* A recepção descobre o interesse no clube durante o
                                            atendimento. Sem este atalho, teria que anotar o nome,
                                            ir em Assinaturas e procurar o cliente de novo. A ação
                                            pertence ao cliente, não ao atendimento, por isso fica
                                            junto do nome e não na fileira de botões de status. */}
                                        {canOfferSubscription && (
                                            <button
                                                type="button"
                                                className="appointment-detail-inline-action"
                                                onClick={handleMakeSubscriber}
                                                title="Cadastrar uma assinatura do clube para este cliente"
                                            >
                                                <i className="fas fa-crown"></i>
                                                Tornar assinante
                                            </button>
                                        )}
                                    </>
                                }
                                subvalue={(currentAppointment.customer?.phone || currentAppointment.customer?.document)
                                    ? `${currentAppointment.customer?.phone ? formatPhoneNumber(currentAppointment.customer.phone) : ''}${currentAppointment.customer?.phone && currentAppointment.customer?.document ? ' • ' : ''}${currentAppointment.customer?.document || ''}`
                                    : null}
                                wide
                            />
                            {currentAppointment.customer?.branch?.name && (
                                <AppointmentDetailItem
                                    icon="fa-solid fa-location-dot"
                                    label="Unidade de origem"
                                    value={currentAppointment.customer.branch.name}
                                    wide
                                />
                            )}
                            {/* O plano é exibido mesmo em atraso (é o que o atendente usa para cobrar),
                                mas com o estado explícito no valor para não parecer benefício liberado. */}
                            {currentAppointment.customer?.clubPlanName && (
                                <AppointmentDetailItem
                                    icon="fa-solid fa-gem"
                                    label="Plano do Clube"
                                    value={currentAppointment.customer.clubLate
                                        ? `${currentAppointment.customer.clubPlanName} (mensalidade em atraso)`
                                        : currentAppointment.customer.clubPlanName}
                                    wide
                                />
                            )}
                            <AppointmentDetailItem
                                icon="fa-solid fa-user-tie"
                                label="Funcionário"
                                value={getDisplayName(currentAppointment.employee)}
                                wide
                            />
                            <AppointmentDetailItem
                                icon="fa fa-list-ul"
                                label={serviceItems.length > 1 ? 'Serviços' : 'Serviço'}
                                value={servicesLabel}
                            />
                            <AppointmentDetailItem
                                icon="fa-regular fa-calendar-days"
                                label="Data"
                                value={moment(currentAppointment.scheduledDate).format('DD/MM/YYYY')}
                            />
                            <AppointmentDetailItem
                                icon="fa-regular fa-clock"
                                label="Hora inicial"
                                value={moment(currentAppointment.scheduledDate).format('HH:mm')}
                            />
                            <AppointmentDetailItem
                                icon="fa-solid fa-hourglass-end"
                                label="Hora final"
                                value={moment(currentAppointment.scheduledDate)
                                    .add(appointmentDuration, 'minutes')
                                    .format('HH:mm')}
                            />
                            <AppointmentDetailItem
                                icon="fa-solid fa-stopwatch"
                                label="Duração"
                                value={appointmentDuration ? `${appointmentDuration} minutos` : '-'}
                            />
                            <AppointmentDetailItem
                                icon="fa-solid fa-tag"
                                label="Preço"
                                value={formatAppointmentPrice(getAppointmentPriceValue(currentAppointment))}
                            />
                            {currentAppointment.canceledBy && (
                                <AppointmentDetailItem
                                    icon="fa-solid fa-user-slash"
                                    label="Cancelado por"
                                    value={currentAppointment.canceledBy}
                                />
                            )}
                            {currentAppointment.cancellationDate && (
                                <AppointmentDetailItem
                                    icon="fa-regular fa-calendar-xmark"
                                    label="Data do cancelamento"
                                    value={formatDateTime(currentAppointment.cancellationDate)}
                                />
                            )}
                            <AppointmentDetailItem
                                icon="fa-solid fa-fingerprint"
                                label="UUID"
                                value={currentAppointment.uuid || '-'}
                                wide
                                code
                            />
                        </div>
                        {currentAppointment.notes && (
                            <div className="appointment-notes-box">
                                <span className="appointment-detail-label">
                                    <i className="fa-regular fa-note-sticky"></i>
                                    <span>Observações</span>
                                </span>
                                <p className="appointment-notes-text mb-0">{currentAppointment.notes}</p>
                            </div>
                        )}
                        {/* Reserva do horário: aparece só quando o serviço exige ou já existe uma reserva.
                            Ao receber ou devolver, o detalhe é recarregado para a agenda atualizar o ícone. */}
                        <AppointmentDepositPanel
                            appointment={currentAppointment}
                            serviceItems={serviceItems}
                            price={getAppointmentPriceValue(currentAppointment)}
                            onChanged={refreshAppointment}
                        />
                        {(salePerformedItems.length > 0 || saleRemovedItems.length > 0) && (
                            /* O que a venda cobrou é o que o cliente realmente fez. A lista de serviços
                               logo acima é o que ele marcou, e as duas divergem sempre que a troca
                               acontece na cadeira, que é o caso que a recepção precisa enxergar aqui. */
                            <div className="appointment-notes-box">
                                <span className="appointment-detail-label">
                                    <i className="fa-solid fa-clipboard-check"></i>
                                    <span>O que foi feito</span>
                                </span>
                                <ul className="list-unstyled mb-0 mt-2">
                                    {salePerformedItems.map((item, index) => (
                                        <li key={`performed-${index}`} className="d-flex justify-content-between align-items-center py-1">
                                            <span>
                                                {item.qty > 1 ? `${item.qty}x ` : ''}{item.productName}
                                                {item.added && (
                                                    <span className="badge badge-sm light badge-success ms-2">adicionado</span>
                                                )}
                                            </span>
                                            <strong>{formatAppointmentPrice(item.total)}</strong>
                                        </li>
                                    ))}
                                    {saleRemovedItems.map((item, index) => (
                                        <li key={`removed-${index}`} className="d-flex justify-content-between align-items-center py-1 text-muted">
                                            <span>
                                                <s>{item.productName}</s>
                                                <span className="badge badge-sm light badge-secondary ms-2">não realizado</span>
                                            </span>
                                            <s>{formatAppointmentPrice(item.total)}</s>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <hr className="appointment-dashboard-details-card__divider" />
                        {/* Os botões são agrupados por intenção: o que avança o atendimento, o que
                            corrige, e o que encerra sem atender. Numa fileira única eram até oito
                            botões de mesmo peso, e o operador tinha que ler todos para achar o
                            próximo passo. */}
                        <div className="appointment-dashboard-details-card__actions-row">
                            <div className="appointment-dashboard-details-card__action-groups">
                                <div className="appointment-dashboard-details-card__actions appointment-dashboard-details-card__actions--primary">
                                {/* Venda rápida: um único clique percorre confirmar -> iniciar -> finalizar
                                    para qualquer status em aberto, liberando o "Gerar Venda" sem etapas intermediárias. */}
                                {expressSale === true && ['SCHEDULED', 'CONFIRMED', 'IN_PROGRESS'].includes(currentAppointment.appointmentStatus) && (
                                    <Button onClick={handleExpressComplete} className="appointment-dashboard-action-btn is-complete">
                                        <i className="fas fa-bolt me-2"></i>Concluir e Gerar Venda
                                    </Button>
                                )}
                                {expressSale === false && currentAppointment.appointmentStatus === 'SCHEDULED' && (
                                    <Button onClick={() => setShowConfirmModal(true)} className="appointment-dashboard-action-btn is-confirm">
                                        <i className="fas fa-check-circle me-2"></i>Confirmar
                                    </Button>
                                )}
                                {currentAppointment.appointmentStatus === 'SCHEDULED' && (
                                    <Button variant="success" onClick={handleSendWhatsappConfirmation}
                                        disabled={sendingWhatsappConfirmation}
                                        className="appointment-dashboard-action-btn">
                                    <i className="fa-brands fa-whatsapp me-2"></i>
                                    {/* O atendente envia o link; a confirmação efetiva é feita pelo cliente na página pública. */}
                                    {sendingWhatsappConfirmation
                                            ? 'Preparando envio...'
                                            : currentAppointment.whatsappConfirmationSentAt
                                                ? 'Reenviar confirmação pelo WhatsApp'
                                                : 'Enviar confirmação pelo WhatsApp'}
                                </Button>
                                )}
                                {expressSale === false && currentAppointment.appointmentStatus === 'CONFIRMED' && (
                                    <Button onClick={() => setShowStartModal(true)} className="appointment-dashboard-action-btn is-start">
                                        <i className="fas fa-play-circle me-2"></i>Iniciar Atendimento
                                    </Button>
                                )}
                                {expressSale === false && currentAppointment.appointmentStatus === 'IN_PROGRESS' && (
                                    <Button onClick={() => setShowCompleteModal(true)} className="appointment-dashboard-action-btn is-complete">
                                        <i className="fas fa-check-double me-2"></i>Finalizar
                                    </Button>
                                )}
                                {currentAppointment.appointmentStatus === 'COMPLETED' && !saleAlreadyGenerated && (
                                    // INFO(Devs): Redireciona para o Caixa (Nova Venda) passando o UUID do agendamento.
                                    // Com isso, a tela de Venda irá pré-carregar os dados deste agendamento.
                                    <Button onClick={() => navigate(`${MenuConfig.sales.order.route}/appointment/${currentAppointment.uuid}`)} className="appointment-dashboard-action-btn is-complete" style={{ backgroundColor: '#17a2b8', borderColor: '#17a2b8', color: '#fff' }}>
                                        <i className="fas fa-cash-register me-2"></i>Gerar Venda
                                    </Button>
                                )}
                                {currentAppointment.appointmentStatus === 'COMPLETED' && saleAlreadyGenerated && (
                                    // Venda já registrada: sinaliza e não permite gerar uma nova para o mesmo agendamento.
                                    <span className="badge bg-secondary d-inline-flex align-items-center" style={{ fontSize: '0.85rem', padding: '0.5rem 0.75rem' }}>
                                        <i className="fas fa-circle-check me-2"></i>Venda já registrada
                                    </span>
                                )}
                                </div>

                                {/* Ajustes: corrigem o atendimento sem mudar o status. */}
                                {(['SCHEDULED', 'CONFIRMED', 'IN_PROGRESS'].includes(currentAppointment.appointmentStatus)
                                    || (currentAppointment.appointmentStatus === 'COMPLETED' && !saleAlreadyGenerated)) && (
                                    <div className="appointment-dashboard-details-card__actions appointment-dashboard-details-card__actions--adjust">
                                        <span className="appointment-dashboard-details-card__actions-label">Ajustes</span>
                                        {['SCHEDULED', 'CONFIRMED'].includes(currentAppointment.appointmentStatus) && (
                                            <>
                                                <Button variant="outline-primary" onClick={() => onAddServices?.(currentAppointment)} className="appointment-dashboard-action-btn is-outline">
                                                    <i className="fas fa-list-check me-2"></i>Serviços
                                                </Button>
                                                <Button variant="outline-primary" onClick={handleEdit} className="appointment-dashboard-action-btn is-outline">
                                                    <i className="fas fa-pen me-2"></i>Editar
                                                </Button>
                                                <Button variant="outline-primary" onClick={() => onReschedule?.(currentAppointment)} className="appointment-dashboard-action-btn is-outline">
                                                    <i className="fas fa-calendar-alt me-2"></i>Reagendar
                                                </Button>
                                            </>
                                        )}
                                        {['SCHEDULED', 'CONFIRMED', 'IN_PROGRESS'].includes(currentAppointment.appointmentStatus) && getDurationInMinutes(currentAppointment) > 10 && (
                                            <Button variant="outline-primary" onClick={() => setShowDurationModal(true)} className="appointment-dashboard-action-btn is-outline">
                                                <i className="fas fa-clock me-2"></i>Ajustar duração
                                            </Button>
                                        )}
                                        <Button variant="outline-warning" onClick={handleEncaixe} className="appointment-dashboard-action-btn is-outline">
                                            <i className="fas fa-arrows-to-dot me-2"></i>Encaixar neste horário
                                        </Button>
                                    </div>
                                )}

                                {/* Encerrar sem atender. Fica por último e separado para não ser
                                    clicado por engano no lugar de uma ação de andamento. */}
                                {(['SCHEDULED', 'CONFIRMED', 'IN_PROGRESS'].includes(currentAppointment.appointmentStatus)
                                    // O no-show pode ter sido marcado por engano; permitir cancelá-lo
                                    // deixa a recepção corrigir o registro sem reabrir o agendamento.
                                    || currentAppointment.appointmentStatus === 'NO_SHOW'
                                    || (currentAppointment.appointmentStatus === 'COMPLETED' && !saleAlreadyGenerated)) && (
                                    <div className="appointment-dashboard-details-card__actions appointment-dashboard-details-card__actions--exit">
                                        <span className="appointment-dashboard-details-card__actions-label">
                                            {currentAppointment.appointmentStatus === 'COMPLETED'
                                                ? 'Cancelar atendimento'
                                                : currentAppointment.appointmentStatus === 'NO_SHOW'
                                                    ? 'Corrigir registro'
                                                    : 'Encerrar sem atender'}
                                        </span>
                                        <Button variant="outline-danger" onClick={() => setShowCancelModal(true)} className="appointment-dashboard-action-btn is-outline">
                                            <i className="fas fa-ban me-2"></i>Cancelar
                                        </Button>
                                        {['SCHEDULED', 'CONFIRMED', 'IN_PROGRESS'].includes(currentAppointment.appointmentStatus) && (
                                            <Button variant="outline-dark" onClick={() => setShowNoShowModal(true)} className="appointment-dashboard-action-btn is-outline-dark">
                                                <i className="fas fa-user-times me-2"></i>Não Compareceu
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </div>
                            <Button variant="outline-secondary" onClick={handleClose} className="appointment-dashboard-action-btn is-close">
                                <i className="fas fa-times me-2"></i>Fechar
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            </Modal>

            {/* Abre por cima do detalhe, como as confirmações abaixo: ao fechar, o operador
                continua no mesmo atendimento. */}
            <CustomerQuickCreateModal
                show={showCustomerEdit}
                customerId={customer?.id}
                onHide={() => setShowCustomerEdit(false)}
                onUpdated={handleCustomerUpdated}
            />

            <ModalPageEvent
                title="Confirmar Agendamento"
                show={showConfirmModal}
                setShow={setShowConfirmModal}
                btnText="Confirmar"
                btnOnClick={handleConfirm}
                centered
                dialogClassName="appointment-cancel-modal"
                child={<p>Tem certeza que deseja confirmar este agendamento?</p>}
            />

            <ModalPageEvent
                title="Iniciar Atendimento"
                show={showStartModal}
                setShow={setShowStartModal}
                btnText="Iniciar"
                btnOnClick={handleStart}
                centered
                dialogClassName="appointment-cancel-modal"
                child={<p>Tem certeza que deseja iniciar o atendimento?</p>}
            />

            <ModalPageEvent
                title="Finalizar Atendimento"
                show={showCompleteModal}
                setShow={setShowCompleteModal}
                btnText="Finalizar"
                btnOnClick={handleComplete}
                centered
                dialogClassName="appointment-cancel-modal"
                child={<p>Tem certeza que deseja finalizar o atendimento?</p>}
            />

            <Modal show={showDurationModal} onHide={() => setShowDurationModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Ajustar duração</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-3">
                        Selecione a nova duração prevista. O restante ficará livre imediatamente na agenda.
                    </p>
                    <div className="d-flex flex-wrap gap-2">
                        {Array.from(
                            { length: Math.max(Math.floor((getDurationInMinutes(currentAppointment) - 10) / 10), 0) },
                            (_, index) => (index + 1) * 10
                        ).map((duration) => (
                            <Button key={duration} variant="outline-primary" onClick={() => handleAdjustDuration(duration)}>
                                {duration} min
                            </Button>
                        ))}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDurationModal(false)}>Cancelar</Button>
                </Modal.Footer>
            </Modal>

            <ModalPageEvent
                title="Cancelar Agendamento"
                show={showCancelModal}
                setShow={setShowCancelModal}
                btnText="Cancelar"
                btnOnClick={handleCancel}
                centered
                dialogClassName="appointment-cancel-modal"
                child={
                    <div>
                        <p className="mb-2">Informe o motivo do cancelamento:</p>
                        <TextArea
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            placeholder="Digite o motivo do cancelamento..."
                            maxLength={255}
                            counter="true"
                            style={{ minHeight: '180px' }}
                        />
                    </div>
                }
            />

            <ModalPageEvent
                title="Marcar como Não Compareceu"
                show={showNoShowModal}
                setShow={setShowNoShowModal}
                btnText="Confirmar"
                btnOnClick={handleNoShow}
                centered
                dialogClassName="appointment-cancel-modal"
                child={<p>Tem certeza que deseja marcar este agendamento como "não compareceu"?</p>}
            />
        </>
    );
};

export default AppointmentDetailsModal;
